import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  type Binding,
  type EmbeddedViewRef,
  Injectable,
  RendererFactory2,
  TemplateRef,
  type Type,
  type ViewRef,
  type WritableSignal,
  createComponent,
  inject,
  inputBinding,
  outputBinding,
  twoWayBinding,
} from '@angular/core';
import { fromEvent, take } from 'rxjs';
import type {
  InputBindings,
  OutputBindings,
  TwoWayBindings,
} from '../../cdk/types';
import { ActiveDialog } from './active-dialog';
import { ConfirmComponent } from './confirm/confirm.component';
import { DialogBuilder } from './dialog-builder';
import { PromptComponent } from './prompt/prompt.component';

type Bindings<C> = {
  inputs?: InputBindings<C>;
  outputs?: OutputBindings<C>;
  twoWay?: TwoWayBindings<C>;
};

type ConfirmBindings = {
  title: string;
  text: string;
  confirmButtonText: string;
  rejectButtonText?: string;
};

type PromptBindings = {
  title: string;
  text: string;
  confirmButtonText: string;
  rejectButtonText?: string;
  inputLabel?: string;
  inputType?: 'text' | 'password';
  inputPlaceholder?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  readonly #appRef = inject(ApplicationRef);
  readonly #body = inject(DOCUMENT).body;
  readonly #renderer = inject(RendererFactory2).createRenderer(null, null);

  readonly #dialogs: DialogBuilder<any>[] = [];

  get active() {
    return this.#dialogs.at(this.#dialogs.length - 1)?.instance;
  }

  create<C>(
    content: Type<C> | TemplateRef<C>,
    bindings?: Bindings<C>,
  ): DialogBuilder<DialogResult<C>> {
    const { dialog, container } = this.#createDialog();
    const activeDialog = new ActiveDialog<DialogResult<C>>(dialog);
    const dialogBuilder = new DialogBuilder(dialog, activeDialog);
    this.#dialogs.push(dialogBuilder);

    let viewRef: ViewRef;
    if (content instanceof TemplateRef) {
      viewRef = this.#attachTemplate(content, activeDialog);
      const nodes = (viewRef as EmbeddedViewRef<unknown>).rootNodes;
      for (const node of nodes) {
        this.#renderer.appendChild(container, node);
      }
    } else {
      viewRef = this.#attachComponent(content, container, bindings);
    }

    this.#onClose(dialog, viewRef);

    return dialogBuilder;
  }

  confirm(config: ConfirmBindings) {
    return this.create(ConfirmComponent, {
      inputs: {
        title: () => config.title,
        text: () => config.text,
        confirmButtonText: () => config.confirmButtonText,
        rejectButtonText: () => config.rejectButtonText || null,
      },
    })
      .withConfig({ customClasses: ['md:w-[600px]'], dismissable: false })
      .open().result;
  }

  prompt(config: PromptBindings) {
    return this.create(PromptComponent, {
      inputs: {
        title: () => config.title,
        text: () => config.text,
        confirmButtonText: () => config.confirmButtonText,
        rejectButtonText: () => config.rejectButtonText || null,
        inputLabel: () => config.inputLabel || null,
        inputType: () => config.inputType || 'text',
        inputPlaceholder: () => config.inputPlaceholder || '',
      },
    })
      .withConfig({ customClasses: ['md:w-[600px]'], dismissable: false })
      .open().result;
  }

  #createDialog() {
    const dialog = this.#renderer.createElement('dialog');
    const container = this.#renderer.createElement('section');
    const classes = ['p-6', 'md:max-w-[664px]', 'overflow-y-auto'];
    for (const name of classes) {
      this.#renderer.addClass(container, name);
    }
    this.#renderer.appendChild(dialog, container);
    this.#renderer.setAttribute(
      dialog,
      'id',
      `tvd-dialog-${this.#dialogs.length}`,
    );
    this.#renderer.setStyle(this.#body, 'overflow', 'hidden');
    this.#renderer.appendChild(this.#body, dialog);
    return { dialog, container };
  }

  #attachComponent<C>(
    component: Type<C>,
    host: HTMLElement,
    bindings?: Bindings<C>,
  ) {
    const nativeBindings: Binding[] = [];
    if (bindings?.inputs) {
      for (const [key, value] of Object.entries(bindings.inputs)) {
        nativeBindings.push(
          inputBinding(key as string, value as () => unknown),
        );
      }
    }
    if (bindings?.outputs) {
      for (const [key, value] of Object.entries(bindings.outputs)) {
        nativeBindings.push(
          outputBinding(
            key as string,
            value as unknown as (event: unknown) => unknown,
          ),
        );
      }
    }
    if (bindings?.twoWay) {
      for (const [key, value] of Object.entries(bindings.twoWay)) {
        nativeBindings.push(
          twoWayBinding(key as string, value as WritableSignal<unknown>),
        );
      }
    }
    const componentRef = createComponent(component, {
      environmentInjector: this.#appRef.injector,
      hostElement: host,
      bindings: nativeBindings,
    });

    this.#appRef.attachView(componentRef.hostView);
    return componentRef.hostView;
  }

  #attachTemplate<C>(
    template: TemplateRef<unknown>,
    activeDialog: ActiveDialog<DialogResult<C>>,
  ) {
    const context = {
      $implicit: activeDialog,
    };

    const viewRef = template.createEmbeddedView(context);
    this.#appRef.attachView(viewRef);
    return viewRef;
  }

  #onClose(dialog: HTMLDialogElement, viewRef: ViewRef) {
    fromEvent(dialog, 'close')
      .pipe(take(1))
      .subscribe(() => {
        this.#dialogs.pop();
        if (!this.#dialogs.length) {
          this.#renderer.setStyle(this.#body, 'overflow', 'auto');
        }
        this.#appRef.detachView(viewRef);
        viewRef.destroy();
        dialog.remove();
      });
  }
}

/** Implementing this interface on modal components
 * lets the `DialogService` infer the result of the components on close
 * Example is in the `dialog-demo.component.ts`
 * */
export interface ActiveDialogRef<Result> {
  onClose: (result?: Result) => void;
}

type DialogResult<C> = C extends ActiveDialogRef<infer R> ? R : unknown;
